import React, { useRef, ReactNode, useState, useEffect } from "react";
import "./TagNavigator.scss";
import { Button, Division, Icon, Icons } from "@ir/ui";

interface TagNavigatorProps {
  tagsData: { key: string; tag: ReactNode }[];
  onDelete: (key: string) => void;
  nonDeletableKeys?: string[];
}

const TagNavigator: React.FC<TagNavigatorProps> = ({ tagsData, onDelete, nonDeletableKeys = [] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const updateScrollState = () => {
      if (scrollRef.current) {
        setCanScrollLeft(scrollRef.current.scrollLeft > 0);
        setCanScrollRight(scrollRef.current.scrollLeft + scrollRef.current.clientWidth < scrollRef.current.scrollWidth);
        setIsScrollable(scrollRef.current.scrollWidth > scrollRef.current.clientWidth);
      }
    };

    updateScrollState();
    globalThis.addEventListener("resize", updateScrollState);
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", updateScrollState);
    }
    return () => {
      globalThis.removeEventListener("resize", updateScrollState);
      if (scrollElement) {
        scrollElement.removeEventListener("scroll", updateScrollState);
      }
    };
  }, [tagsData]);

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scrollRef.current) {
      setIsMouseDown(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
    }
  };

  const handleMouseLeave = () => {
    setIsMouseDown(false);
  };

  const handleMouseUp = () => {
    setIsMouseDown(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isMouseDown || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <Division className="tag-navigator">
      {isScrollable && (
        <Button variant="clear" className="scroll-btn left" onPress={() => scroll("left")} isDisabled={!canScrollLeft}>
          <Icon name={Icons.ChevronLeft} />
        </Button>
      )}
      <Division
        className="tags-container"
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        {tagsData.map(({ key, tag }) => (
          <Division key={key} className="tag">
            {React.cloneElement(tag as React.ReactElement<{ onDelete?: () => void }>, nonDeletableKeys.includes(key) ? {} : { onDelete: () => onDelete(key) })}
          </Division>
        ))}
      </Division>
      {isScrollable && (
        <Button variant="clear" className="scroll-btn right" onPress={() => scroll("right")} isDisabled={!canScrollRight}>
          <Icon name={Icons.ChevronRight} />
        </Button>
      )}
    </Division>
  );
};

export { TagNavigator };
